import type { CustomerBalanceTransaction, CustomerOrder } from '@talos/kyoko';
import {
  CustomerBalanceTransactionStatusEnum,
  CustomerBalanceTransactionTypeEnum,
  formattedDate,
  OrdStatusEnum,
  useCurrenciesContext,
  useSecuritiesContext,
} from '@talos/kyoko';
import { Fragment, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { InlineFormattedNumber } from '../components/InlineFormattedNumber';

export function TransactionItem({ entity }: { entity: CustomerOrder | CustomerBalanceTransaction }) {
  const navigate = useNavigate();
  const onHandleClick = useCallback(() => {
    if ('OrderID' in entity) {
      navigate('entity?orderID=' + entity.OrderID);
    } else {
      navigate('entity?transactionID=' + entity.TransactionID);
    }
  }, [entity, navigate]);

  return (
    <div
      className="border-color-border bg-color-bg-main flex size-full w-full flex-col px-4 py-2 tracking-wide"
      onClick={onHandleClick}
      data-testid={'OrderID' in entity ? `order-${entity.OrderID}` : `balance-transaction-${entity.TransactionID}`}
    >
      {'OrderID' in entity ? <OrderItem order={entity} /> : <BalanceTransactionItem transaction={entity} />}
    </div>
  );
}

function OrderItem({ order }: { order: CustomerOrder }) {
  const { currenciesBySymbol } = useCurrenciesContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const orderCurrency =
    order.Currency ?? (order.Symbol ? securitiesBySymbol.get(order.Symbol)?.BaseCurrency : undefined);
  const description = orderCurrency ? currenciesBySymbol.get(orderCurrency)?.Description : undefined;
  return (
    <Fragment>
      <div className="mb-1 flex w-full justify-between">
        <div className="text-color-text-info flex flex-row items-center gap-2 text-xs">
          <span className="font-bold">Trade</span>
          <span className="text-color-text-weakest uppercase">
            {formattedDate(order.Timestamp, '{hh}:{mm} {tt}', 'en-PH')}
          </span>
        </div>
        <TransactionStatus status={order.OrdStatus} />
      </div>
      <div className="text-l flex w-full justify-between font-semibold">
        <span>{description}</span>
        <span>
          <InlineFormattedNumber number={order.CumAmt} currency={order.AmountCurrency} />
        </span>
      </div>
      <div className="text-color-text-weaker flex w-full justify-end text-xs font-semibold">
        <InlineFormattedNumber number={order.CumQty} currency={order.Currency} showCurrency={false} />
      </div>
    </Fragment>
  );
}

function BalanceTransactionItem({ transaction }: { transaction: CustomerBalanceTransaction }) {
  return (
    <Fragment>
      <div className="mb-1 flex w-full justify-between">
        <div className="text-color-text-info flex flex-row items-center gap-2 text-xs">
          <TransactionType type={transaction.TransactionType} />
          <span className="text-color-text-weakest uppercase">
            {formattedDate(transaction.Timestamp, '{hh}:{mm} {tt}', 'en-PH')}
          </span>
        </div>
        <TransactionStatus status={transaction.Status} />
      </div>
      <div className="text-l flex w-full justify-between font-semibold">
        <span>Trading Wallet</span>
        <span>
          <InlineFormattedNumber number={transaction.Quantity} currency={transaction.Currency} />
        </span>
      </div>
      {
        // We cannot show the PHP equivalent since it is not on the transaction object
        /*<div className="flex w-full justify-between">
        <span />
        <span>PHP -----</span>
      </div>*/
      }
    </Fragment>
  );
}

function TransactionType({ type }: { type: CustomerBalanceTransactionTypeEnum }) {
  switch (type) {
    case CustomerBalanceTransactionTypeEnum.Deposit:
      return <span className="font-bold">Top Up</span>;
    case CustomerBalanceTransactionTypeEnum.Withdrawal:
      return <span className="font-bold">Withdraw</span>;
  }
}

function TransactionStatus({ status }: { status: CustomerBalanceTransactionStatusEnum | OrdStatusEnum }) {
  switch (status) {
    case CustomerBalanceTransactionStatusEnum.Completed:
    case OrdStatusEnum.Filled:
      return (
        <span className="bg-color-bg-success-weakest text-color-text-success rounded-xl p-1 px-2 text-xs font-semibold">
          Successful
        </span>
      );
    case OrdStatusEnum.Rejected:
    case CustomerBalanceTransactionStatusEnum.Canceled:
    case CustomerBalanceTransactionStatusEnum.Rejected:
      return (
        <span className="bg-color-bg-error-weakest text-color-text-error rounded-xl p-1 px-2 text-xs font-semibold">
          Unsuccessful
        </span>
      );
    default:
      return (
        <span className="bg-color-bg-warning-weakest text-color-text-warning rounded-xl p-1 px-2 text-xs font-semibold">
          Pending
        </span>
      );
  }
}
